import { AddressDisplay } from '@liveauctioneers/hammer-ui-core/addressDisplay';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useEffect, useState } from 'react';

import { BidderAddressList } from '@/components/BidderAddressList/BidderAddressList';
import { ShippingMethods } from '@/enums/shippingMethod';
import { useAppSelector } from '@/redux/hooks';
import { getDefaultAddressId, getUserAddressById, getUserData } from '@/redux/modules/account/user/user.selectors';
import { BidderAddress } from '@/types/BidderAddresses';

type Props = {
    active: boolean;
    addressId: number;
    onReviewStep: boolean;
    onShippingAddressChanged: (address: BidderAddress, valid: boolean) => void;
    shippingMethod: ShippingMethods;
};

const Shipping = ({ active, addressId, onReviewStep, onShippingAddressChanged, shippingMethod }: Props) => {
    const { addresses } = useAppSelector(getUserData);

    const addressInList = addresses?.findIndex((address) => address.addressId === addressId) !== -1;

    const defaultAddressId = useAppSelector((state) => getDefaultAddressId(state));
    const addressToUseId = addressId ? addressId : defaultAddressId;
    const addressToUseFull = useAppSelector((state) => getUserAddressById(state, addressToUseId));

    const [addressToUse, setAddressToUse] = useState(addressToUseFull);

    // when an address is suddenly not in the list, due to an update or deletion, we need to make sure
    // some address is used
    useEffect(() => {
        if (!addressInList && Boolean(addresses.length)) {
            setAddressToUse(addresses[0]);
            onShippingAddressChanged(addresses[0], true);
        } else {
            setAddressToUse(addressToUseFull);
            onShippingAddressChanged(addressToUseFull, true);
        }
    }, [addressInList, addressToUseFull, addresses, onShippingAddressChanged]);

    const selectAddress = (address: BidderAddress) => {
        setAddressToUse(address);
        onShippingAddressChanged(address, true);
    };

    if (!active && shippingMethod === ShippingMethods.Pickup) {
        return (
            <BodySecondary color="secondary">
                <FormattedMessage id="checkout.shipping.pickup" />
            </BodySecondary>
        );
    }

    if (!active && Boolean(addressToUse)) {
        return (
            <AddressDisplay
                address={addressToUse}
                testid="address"
                variant={onReviewStep ? 'primary' : 'secondary'}
            />
        );
    }

    return (
        <BidderAddressList
            addresses={addresses}
            canEdit
            selectAddress={selectAddress}
            selectedAddress={addressToUse}
            setDefaultOnSelect={false}
        />
    );
};

export default Shipping;
